import type { App } from 'vue'
import { VERSION } from '@cinqo/common'
import * as Sentry from '@sentry/vue'

export function setupSentry(app: App, environment: string) {
  try {
    // Check if Sentry is blocked
    const isSentryBlocked = !window.fetch || document.querySelector('script[src*="sentry"]')?.getAttribute('blocked') === 'true'

    if (isSentryBlocked) {
      console.warn('Sentry appears to be blocked. Continuing without error tracking.')

      // Provide mock Sentry functions to prevent errors
      window.__SENTRY__ = {
        hub: {
          addBreadcrumb: () => {},
          captureException: () => {},
          captureMessage: () => {},
        },
      }

      return false
    }

    Sentry.init({
      app,
      dsn: 'https://be7b515ff7075949cc5738f110fe7e25@o4509168577085440.ingest.de.sentry.io/4509168578330704',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracePropagationTargets: [
        'https://api.cinqo.fr',
        'https://app.cinqo.fr',
        'https://api-staging.cinqo.fr',
        'https://staging.cinqo.fr',
      ],
      beforeSend(event) {
        // Don't send events if Sentry appears to be blocked
        if (isSentryBlocked)
          return null

        // Add environment tags only if not in development
        if (environment !== 'development' && event.tags) {
          event.tags.environment = environment
          event.tags.version = VERSION
        }

        return event
      },
      environment,
      release: VERSION,
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,
    })

    // Set global tags for all events, only when not in development
    if (environment !== 'development') {
      Sentry.setTag('environment', environment)
      Sentry.setTag('version', VERSION)
    }

    return true
  }
  catch (error) {
    console.warn('Failed to initialize Sentry:', error)
    return false
  }
}

// Optional: Create a wrapper for error logging
export const errorLogger = {
  captureException: (error: Error, context?: Record<string, any>) => {
    try {
      Sentry.captureException(error, context)
    }
    catch {
      console.error('Error logging failed:', error, context)
    }
  },
  captureMessage: (message: string, level?: Sentry.SeverityLevel) => {
    try {
      Sentry.captureMessage(message, level)
    }
    catch {
      console.error('Message logging failed:', message)
    }
  },
}
